import { useState, useRef, useEffect } from 'react';
import { SmilePlus } from 'lucide-react';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface Reaction {
  id: string;
  emoji: string;
  userId: string;
}

interface EmojiReactionsProps {
  reactions: Reaction[];
  currentUserId?: string;
  onReact: (emoji: string) => void;
  size?: 'sm' | 'md';
  /** Single emoji mode — only shows one emoji, no count, new one replaces old */
  singleMode?: boolean;
}

export function EmojiReactions({ reactions, currentUserId, onReact, size = 'sm', singleMode }: EmojiReactionsProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group reactions by emoji
  const grouped = reactions.reduce<Record<string, { count: number; userIds: string[] }>>((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = { count: 0, userIds: [] };
    acc[r.emoji].count++;
    acc[r.emoji].userIds.push(r.userId);
    return acc;
  }, {});

  const hasReactions = Object.keys(grouped).length > 0;
  const isSmall = size === 'sm';

  // In single mode, just show the latest emoji (no count)
  const singleEmoji = singleMode && reactions.length > 0 ? reactions[reactions.length - 1].emoji : null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {singleMode ? (
        // Single mode: just one emoji, click to change
        singleEmoji && (
          <button
            onClick={() => onReact(singleEmoji)}
            className="text-base hover:scale-110 transition-all"
            title="Click to remove"
          >
            {singleEmoji}
          </button>
        )
      ) : (
        // Multi mode: grouped with counts (for comments)
        Object.entries(grouped).map(([emoji, data]) => {
          const isMine = currentUserId && data.userIds.includes(currentUserId);
          return (
            <button
              key={emoji}
              onClick={() => onReact(emoji)}
              className={`inline-flex items-center gap-1 rounded-full border transition-all hover:scale-105 ${
                isSmall ? 'px-2 py-0.5 text-sm' : 'px-2.5 py-1 text-base'
              } ${
                isMine
                  ? 'bg-primary-50 border-primary-300 text-primary-700 shadow-sm'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
              }`}
              title={isMine ? 'Click to remove' : 'Click to react'}
            >
              <span className={isSmall ? 'text-sm' : 'text-base'}>{emoji}</span>
              <span className={`font-semibold ${isSmall ? 'text-xs' : 'text-sm'}`}>{data.count}</span>
            </button>
          );
        })
      )}

      {/* Add reaction button */}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className={`inline-flex items-center justify-center rounded-full border transition-all hover:scale-105 ${
            showPicker
              ? 'border-primary-300 bg-primary-50 text-primary-500'
              : 'border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 hover:border-gray-300'
          } ${
            isSmall
              ? hasReactions ? 'w-6 h-6' : 'w-6 h-6'
              : 'w-7 h-7'
          }`}
          title="Add reaction"
        >
          <SmilePlus className={isSmall ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        </button>

        {/* Quick emoji picker */}
        {showPicker && (
          <>
            <div className="fixed inset-0 z-[99]" onClick={() => setShowPicker(false)} />
            <div
              className="fixed z-[100] rounded-full p-1.5 flex gap-0.5 shadow-lg"
              style={{
                bottom: window.innerHeight - (pickerRef.current?.getBoundingClientRect().top ?? 0) + 8,
                left: Math.max(8, Math.min(
                  (pickerRef.current?.getBoundingClientRect().left ?? 0) - 100,
                  window.innerWidth - 240
                )),
                background: '#fff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              {QUICK_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onReact(emoji);
                    setShowPicker(false);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-90 text-base transition-all"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
